import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import {
  appendConsentLog,
  needsTermsOrPrivacyReacceptance,
  setAcceptedLegalVersions,
} from '../lib/consentStorage';
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from '../lib/legalVersions';
import { fetchClientIp } from '../lib/fetchClientIp';
import { useAuth } from '../contexts/AuthContext';
import { subscribeOnboardingFlowComplete } from '../lib/onboardingLifecycle';

type SessionUser = NonNullable<ReturnType<typeof useAuth>['user']>;

/**
 * Terms re-acceptance as a plain portaled layer (not Radix Dialog).
 * Radix Dialog + COMO onboarding stacking was leaving a dim overlay with only the close
 * control visible; this keeps the same UX without Radix scroll-lock / animation issues.
 */
function TermsReacceptancePanel({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const accept = async () => {
    const ip = await fetchClientIp();
    setAcceptedLegalVersions(CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION);
    appendConsentLog({
      user_id: user.id,
      email: user.email,
      terms_version: CURRENT_TERMS_VERSION,
      privacy_version: CURRENT_PRIVACY_VERSION,
      ip_address: ip,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      event: 'terms_reaccept',
    });
    setOpen(false);
  };

  const viewTerms = () => {
    setOpen(false);
    navigate('/legal/terms-of-service');
  };

  if (!open) return null;

  const panel = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-update-title"
      className="fixed inset-0 z-[10150] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        className="relative w-full max-w-lg rounded-xl border border-cyan-500/35 bg-zinc-950 p-6 pt-12 shadow-2xl text-zinc-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute right-3 top-3 rounded-md p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          aria-label="Close"
        >
          <X className="size-5 shrink-0" />
        </button>
        <h2 id="terms-update-title" className="pr-10 text-lg font-semibold leading-tight text-white">
          Terms updated
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
          We have updated our Terms of Service and Privacy Policy. Please review and accept the updated terms to continue
          using CoreMonit.
        </p>
        <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={viewTerms}>
            View Terms
          </Button>
          <Button className="bg-gradient-to-r from-cyan-500 to-blue-600" onClick={accept}>
            Accept &amp; Continue
          </Button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
}

export function TermsUpdateModal() {
  const { user } = useAuth();
  const [onboardingFlowComplete, setOnboardingFlowComplete] = useState(false);
  const [panelMountReady, setPanelMountReady] = useState(false);

  React.useEffect(() => {
    return subscribeOnboardingFlowComplete(() => setOnboardingFlowComplete(true));
  }, []);

  React.useEffect(() => {
    if (!user || !onboardingFlowComplete || !needsTermsOrPrivacyReacceptance()) {
      setPanelMountReady(false);
      return;
    }

    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setPanelMountReady(true);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [user, onboardingFlowComplete]);

  if (!user) return null;
  if (!onboardingFlowComplete || !needsTermsOrPrivacyReacceptance()) return null;
  if (!panelMountReady) return null;

  return <TermsReacceptancePanel user={user} key={`terms-reaccept-${user.id}`} />;
}
